import { z } from 'zod';
import { createRpcContract, createRpcCaller } from '@/lib/rpc';

// ============================================
// TYPES
// ============================================

export interface ProgramSubmission {
  id: string;
  authorId: string;
  authorName: string;
  authorEmail: string;
  name: string;
  description: string | null;
  level: 'beginner' | 'intermediate' | 'advanced';
  focus: string;
  durationWeeks: number;
  daysPerWeek: number;
  equipment: string[];
  status: 'pending' | 'approved' | 'rejected';
  submittedAt: Date;
  reviewedAt: Date | null;
  reviewedBy: string | null;
  rejectionReason: string | null;
}

export interface ProgramSubmissionDetail extends ProgramSubmission {
  weeks: ProgramWeek[];
}

export interface ProgramWeek {
  weekNumber: number;
  days: ProgramDay[];
}

export interface ProgramDay {
  dayNumber: number;
  name: string;
  exercises: ProgramExercise[];
}

export interface ProgramExercise {
  id: string;
  exerciseName: string;
  order: number;
  sets: number;
  repsMin: number;
  repsMax: number;
  rpe: number | null;
  restSeconds: number;
  notes: string | null;
}

// ============================================
// RPC CONTRACTS
// ============================================

// Get pending submissions
export const getPendingSubmissionsContract = createRpcContract(
  'admin_get_pending_submissions',
  z.object({
    p_limit: z.number().optional(),
    p_offset: z.number().optional(),
    p_status: z.enum(['pending', 'approved', 'rejected']).optional(),
  }),
  z.object({
    submissions: z.array(
      z.object({
        id: z.string(),
        author_id: z.string(),
        author_name: z.string(),
        author_email: z.string(),
        name: z.string(),
        description: z.string().nullable(),
        level: z.enum(['beginner', 'intermediate', 'advanced']),
        focus: z.string(),
        duration_weeks: z.number(),
        days_per_week: z.number(),
        equipment: z.array(z.string()),
        status: z.enum(['pending', 'approved', 'rejected']),
        submitted_at: z.string(),
        reviewed_at: z.string().nullable(),
        reviewed_by: z.string().nullable(),
        rejection_reason: z.string().nullable(),
      })
    ),
    total: z.number(),
  })
);

// Get submission detail
export const getSubmissionDetailContract = createRpcContract(
  'admin_get_submission_detail',
  z.object({
    p_submission_id: z.string().uuid(),
  }),
  z.object({
    submission: z.object({
      id: z.string(),
      author_id: z.string(),
      author_name: z.string(),
      author_email: z.string(),
      name: z.string(),
      description: z.string().nullable(),
      level: z.enum(['beginner', 'intermediate', 'advanced']),
      focus: z.string(),
      duration_weeks: z.number(),
      days_per_week: z.number(),
      equipment: z.array(z.string()),
      status: z.enum(['pending', 'approved', 'rejected']),
      submitted_at: z.string(),
      reviewed_at: z.string().nullable(),
      reviewed_by: z.string().nullable(),
      rejection_reason: z.string().nullable(),
    }),
    weeks: z.array(
      z.object({
        week_number: z.number(),
        days: z.array(
          z.object({
            day_number: z.number(),
            name: z.string(),
            exercises: z.array(
              z.object({
                id: z.string(),
                exercise_name: z.string(),
                order: z.number(),
                sets: z.number(),
                reps_min: z.number(),
                reps_max: z.number(),
                rpe: z.number().nullable(),
                rest_seconds: z.number(),
                notes: z.string().nullable(),
              })
            ),
          })
        ),
      })
    ),
  })
);

// Approve submission
export const approveSubmissionContract = createRpcContract(
  'admin_approve_submission',
  z.object({
    p_submission_id: z.string().uuid(),
    p_is_featured: z.boolean().optional(),
  }),
  z.object({
    success: z.boolean(),
  })
);

// Reject submission
export const rejectSubmissionContract = createRpcContract(
  'admin_reject_submission',
  z.object({
    p_submission_id: z.string().uuid(),
    p_reason: z.string(),
  }),
  z.object({
    success: z.boolean(),
  })
);

// ============================================
// RPC CALLERS
// ============================================

export const reviewQueueRpc = {
  getPendingSubmissions: createRpcCaller(getPendingSubmissionsContract),
  getSubmissionDetail: createRpcCaller(getSubmissionDetailContract),
  approveSubmission: createRpcCaller(approveSubmissionContract),
  rejectSubmission: createRpcCaller(rejectSubmissionContract),
};

// ============================================
// TRANSFORM HELPERS
// ============================================

export function transformSubmission(
  raw: z.infer<typeof getPendingSubmissionsContract.outputSchema>['submissions'][number]
): ProgramSubmission {
  return {
    id: raw.id,
    authorId: raw.author_id,
    authorName: raw.author_name,
    authorEmail: raw.author_email,
    name: raw.name,
    description: raw.description,
    level: raw.level,
    focus: raw.focus,
    durationWeeks: raw.duration_weeks,
    daysPerWeek: raw.days_per_week,
    equipment: raw.equipment,
    status: raw.status,
    submittedAt: new Date(raw.submitted_at),
    reviewedAt: raw.reviewed_at ? new Date(raw.reviewed_at) : null,
    reviewedBy: raw.reviewed_by,
    rejectionReason: raw.rejection_reason,
  };
}

export function transformSubmissionDetail(
  raw: z.infer<typeof getSubmissionDetailContract.outputSchema>
): ProgramSubmissionDetail {
  return {
    ...transformSubmission(raw.submission),
    weeks: raw.weeks.map((w) => ({
      weekNumber: w.week_number,
      days: w.days.map((d) => ({
        dayNumber: d.day_number,
        name: d.name,
        exercises: d.exercises.map((e) => ({
          id: e.id,
          exerciseName: e.exercise_name,
          order: e.order,
          sets: e.sets,
          repsMin: e.reps_min,
          repsMax: e.reps_max,
          rpe: e.rpe,
          restSeconds: e.rest_seconds,
          notes: e.notes,
        })),
      })),
    })),
  };
}
